import { useIntl } from '@capsicum/common';
import { amlQuestionnaireReducerAtom } from '@capsicum/features/regulations/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

type SourceOfIncomeEstimateFormType = z.infer<ReturnType<typeof useSourceOfIncomeEstimateFormSchema>>;

const useSourceOfIncomeEstimateFormSchema = () => {
	const { $t } = useIntl();

	return z.object({
		estimatedAnnualIncome: z
			.string()
			.refine(
				val => {
					const num = Number(val);
					const isNumber = !isNaN(num) && num >= 0 && num <= 1000000000;
					const noLeadingZeros = val === String(num);
					const noDecimals = num === Math.floor(num);

					return isNumber && noLeadingZeros && noDecimals;
				},
				{
					message: $t('amlQuestionnaire.thirdStep.errorMessage')
				}
			)
			.transform(val => parseInt(val))
	});
};

export const useAmlSourceOfIncomeEstimate = () => {
	const dispatch = useSetAtom(amlQuestionnaireReducerAtom);

	const sourceOfIncomeEstimateForm = useForm<SourceOfIncomeEstimateFormType>({
		delayError: 200,
		mode: 'onChange',
		resolver: zodResolver(useSourceOfIncomeEstimateFormSchema())
	});

	const formValues = sourceOfIncomeEstimateForm.watch();
	const isValid = sourceOfIncomeEstimateForm.formState.isValid;

	const previousValuesRef = useRef(formValues);
	const previousValidityRef = useRef(isValid);

	useEffect(() => {
		const hasFormValuesChanged = JSON.stringify(previousValuesRef.current) !== JSON.stringify(formValues);
		const hasValidityChanged = previousValidityRef.current !== isValid;

		if (hasFormValuesChanged || hasValidityChanged) {
			previousValuesRef.current = formValues;
			previousValidityRef.current = isValid;

			dispatch({ isValid, stepId: 'EstimatedAnnualIncome', type: 'UPDATE_STEP_VALIDITY' });
			dispatch({
				data: { ...formValues },
				type: 'UPDATE_FORM_DATA'
			});
		}
	}, [formValues, isValid]);

	return {
		sourceOfIncomeEstimateForm
	};
};
